
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Key, Globe, CheckCircle, AlertCircle } from 'lucide-react';

interface PredefinedToolAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolName: string;
  toolId: string;
}

type AuthMethod = 'api-key' | 'oauth';
type OAuthOption = 'developer-app' | 'custom-app';

export const PredefinedToolAuthModal: React.FC<PredefinedToolAuthModalProps> = ({
  isOpen,
  onClose,
  toolName,
  toolId
}) => {
  const [authMethod, setAuthMethod] = useState<AuthMethod>('api-key');
  const [oauthOption, setOAuthOption] = useState<OAuthOption>('developer-app');
  const [apiKeyName, setApiKeyName] = useState('');
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [scopes, setScopes] = useState('');

  const handleSave = () => {
    // TODO: Implement save logic
    console.log('Saving auth config for tool:', toolId, {
      authMethod,
      ...(authMethod === 'api-key' ? {
        apiKeyName,
        apiKeyValue
      } : {
        oauthOption,
        ...(oauthOption === 'custom-app' ? {
          authUrl,
          tokenUrl,
          clientId,
          scopes
        } : {})
      })
    });
    onClose();
  };

  const redirectUrl = 'auth.pullseai.com/oauth/callback';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 text-purple-600">
              <Shield className="h-5 w-5" />
            </div>
            Configure Authentication for {toolName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Auth Method Selection */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Choose Authentication Method</Label>
            <RadioGroup value={authMethod} onValueChange={(value) => setAuthMethod(value as AuthMethod)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="api-key" id="api-key" />
                <Label htmlFor="api-key" className="flex items-center gap-2 cursor-pointer">
                  <Key className="h-4 w-4" />
                  API Key
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="oauth" id="oauth" />
                <Label htmlFor="oauth" className="flex items-center gap-2 cursor-pointer">
                  <Globe className="h-4 w-4" />
                  OAuth 2.0
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* API Key Configuration */}
          {authMethod === 'api-key' && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Key className="h-5 w-5 text-blue-600" />
                  API Key Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="api-key-name">API Key Name</Label>
                  <Input
                    id="api-key-name"
                    value={apiKeyName}
                    onChange={(e) => setApiKeyName(e.target.value)}
                    placeholder="e.g., Authorization, X-API-Key"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="api-key-value">API Key Value</Label>
                  <Input
                    id="api-key-value"
                    type="password"
                    value={apiKeyValue}
                    onChange={(e) => setApiKeyValue(e.target.value)}
                    placeholder="Enter your API key"
                    className="h-12"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* OAuth Configuration */}
          {authMethod === 'oauth' && (
            <div className="space-y-6">
              <Card className="border-green-200 bg-green-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Globe className="h-5 w-5 text-green-600" />
                    OAuth 2.0 Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">OAuth Setup Option</Label>
                    <RadioGroup value={oauthOption} onValueChange={(value) => setOAuthOption(value as OAuthOption)}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="developer-app" id="developer-app" />
                        <Label htmlFor="developer-app" className="flex items-center gap-2 cursor-pointer">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Use Our Developer App (Recommended)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="custom-app" id="custom-app" />
                        <Label htmlFor="custom-app" className="flex items-center gap-2 cursor-pointer">
                          <AlertCircle className="h-4 w-4 text-orange-500" />
                          Use Your Own App
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {oauthOption === 'developer-app' && (
                    <div className="p-4 bg-green-100 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 text-green-800 mb-2">
                        <CheckCircle className="h-4 w-4" />
                        <span className="font-medium">Easy Setup</span>
                      </div>
                      <p className="text-sm text-green-700">
                        We'll handle the OAuth configuration using our pre-configured developer app. 
                        This is the fastest way to get started.
                      </p>
                    </div>
                  )}

                  {oauthOption === 'custom-app' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="auth-url">Authorization URL *</Label>
                          <Input
                            id="auth-url"
                            value={authUrl}
                            onChange={(e) => setAuthUrl(e.target.value)}
                            placeholder="https://api.example.com/oauth/authorize"
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="token-url">Token URL *</Label>
                          <Input
                            id="token-url"
                            value={tokenUrl}
                            onChange={(e) => setTokenUrl(e.target.value)}
                            placeholder="https://api.example.com/oauth/token"
                            className="h-12"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client-id">Client ID *</Label>
                          <Input
                            id="client-id"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            placeholder="your-client-id"
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="redirect-url">Redirect URL</Label>
                          <Input
                            id="redirect-url"
                            value={redirectUrl}
                            readOnly
                            className="h-12 bg-gray-100 cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500">This URL is fixed for security reasons</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="scopes">Scopes (comma separated)</Label>
                        <Input
                          id="scopes"
                          value={scopes}
                          onChange={(e) => setScopes(e.target.value)}
                          placeholder="read, write, user:email"
                          className="h-12"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
              Save Configuration
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
